import { cn } from "@/utils";
import {
  cloneElement,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";

/**
 * Rótulo curto que aparece em cima de um controle ao passar o mouse ou chegar
 * pelo teclado — o "o que este ícone faz" do botão só de ícone.
 *
 * Substitui o `title`, pelos mesmos motivos do `TruncatedText`: o balão nativo
 * demora, sai noutra fonte e não existe para quem navega por teclado.
 *
 * - **Só texto curto.** Explicação longa ou ação dentro do balão é `Modal` ou
 *   `Alert`; o tooltip some quando o ponteiro sai.
 * - **Não substitui o `aria-label`.** O gatilho continua com o nome dele; o
 *   tooltip entra como descrição (`aria-describedby`).
 * - **Posição fixa, acima e centrado no gatilho**, para escapar do recorte da
 *   tabela que rola por dentro; abre para baixo quando não cabe em cima.
 */
export interface TooltipProps {
  /** O texto do balão. */
  content: ReactNode;
  /** Um único elemento focável (normalmente um `Button`). */
  children: ReactElement<{ "aria-describedby"?: string }>;
  className?: string;
}

/** Folga entre o gatilho e o balão. */
const RESPIRO = 6;

/** Folga para o balão não encostar na borda da janela. */
const MARGEM = 8;

export const Tooltip = ({ content, children, className }: TooltipProps) => {
  const id = useId();
  const gatilhoRef = useRef<HTMLSpanElement>(null);
  const balaoRef = useRef<HTMLSpanElement>(null);
  const [visivel, setVisivel] = useState(false);
  const [posicao, setPosicao] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const gatilho = gatilhoRef.current;
    const balao = balaoRef.current;
    if (!visivel || !gatilho || !balao) return;

    const alvo = gatilho.getBoundingClientRect();
    const { width, height } = balao.getBoundingClientRect();

    const x = Math.min(
      Math.max(alvo.left + alvo.width / 2 - width / 2, MARGEM),
      window.innerWidth - width - MARGEM
    );
    const acima = alvo.top - height - RESPIRO;
    const y = acima < MARGEM ? alvo.bottom + RESPIRO : acima;

    setPosicao({ x, y });
  }, [visivel]);

  const abrir = () => setVisivel(true);
  const fechar = () => setVisivel(false);

  return (
    <span
      ref={gatilhoRef}
      className="inline-flex"
      onPointerEnter={abrir}
      onPointerLeave={fechar}
      onFocus={abrir}
      onBlur={fechar}
      onKeyDown={event => {
        if (event.key === "Escape") fechar();
      }}
    >
      {cloneElement(children, {
        "aria-describedby": visivel ? id : undefined,
      })}
      {visivel && (
        <span
          ref={balaoRef}
          id={id}
          role="tooltip"
          style={{ left: posicao.x, top: posicao.y }}
          className={cn(
            "bg-ds-ink-900 text-ds-on-solid font-ds-sans text-ds-caption shadow-ds-overlay rounded-ds-sm pointer-events-none fixed z-[1200] max-w-xs px-2 py-1 font-medium whitespace-nowrap",
            className
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
};
